import { Link, useParams } from "react-router"
import { ArrowLeft, Download } from "lucide-react"

import { useCreosAsset } from "@/api/hooks/creos"
import { Button } from "@/components/ui/button"
import { usePageTitle } from "@/layouts/pageChrome"
import { triggerAuthenticatedDownload } from "@/lib/download"
import { cn } from "@/lib/utils"

function statusBadgeClass(status: string): string {
  switch (status) {
    case "completed":
      return "bg-emerald-500/20 text-emerald-300"
    case "failed":
      return "bg-red-500/20 text-red-300"
    case "generating":
      return "bg-amber-500/20 text-amber-300"
    case "pending":
      return "bg-sky-500/20 text-sky-300"
    default:
      return "bg-white/10 text-[#aaa]"
  }
}

export default function CreosDetailPage() {
  const params = useParams()
  const id = Number(params.id)
  usePageTitle("", Number.isFinite(id) ? `CreOS #${id}` : "CreOS")

  const { data: asset, isLoading, error } = useCreosAsset(id, {
    enabled: Number.isFinite(id) && id > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === "pending" || status === "generating" ? 3000 : false
    },
  })

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          asChild
          variant="outline"
          className="border-[#555] bg-transparent text-white hover:bg-[#333]"
        >
          <Link to="/creos">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
            Back to list
          </Link>
        </Button>
        {asset ? (
          <span
            className={cn(
              "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
              statusBadgeClass(asset.status),
            )}
          >
            {asset.status}
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <p className="text-sm text-[#aaa]">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-300">{error.message}</p>
      ) : !asset ? (
        <p className="text-sm text-[#aaa]">Asset not found</p>
      ) : (
        <>
          <div className="rounded-xl border border-[#4a4a4a] bg-[#2d2d2d] p-6">
            <h1 className="text-xl font-semibold text-white">
              Request #{asset.id}
            </h1>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[#888]">Vertical</dt>
                <dd className="text-white">{asset.vertical_name}</dd>
              </div>
              <div>
                <dt className="text-[#888]">Language</dt>
                <dd className="uppercase text-white">{asset.language}</dd>
              </div>
              <div>
                <dt className="text-[#888]">Slides</dt>
                <dd className="text-white">
                  {asset.slide_count} ({asset.asset_type})
                </dd>
              </div>
              <div>
                <dt className="text-[#888]">Aspect</dt>
                <dd className="text-white">{asset.aspect_ratio}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[#888]">Prompt</dt>
                <dd className="mt-1 whitespace-pre-wrap text-[#ddd]">
                  {asset.buyer_prompt}
                </dd>
              </div>
              {asset.error_message ? (
                <div className="sm:col-span-2">
                  <dt className="text-[#888]">Error</dt>
                  <dd className="mt-1 text-red-300">{asset.error_message}</dd>
                </div>
              ) : null}
            </dl>
          </div>

          {asset.status === "pending" || asset.status === "generating" ? (
            <p className="text-sm text-[#aaa]">
              Generation in progress… this page refreshes automatically.
            </p>
          ) : null}

          {asset.s3_urls?.length ? (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white">Results</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {asset.s3_urls.map((item, idx) => {
                  const previewUrl = item.url
                  const downloadUrl =
                    item.download_url || `${item.url}/download`
                  const filename = `creos-${asset.id}-slide-${idx + 1}.jpg`
                  return (
                    <div
                      key={item.key || idx}
                      className="overflow-hidden rounded-xl border border-[#4a4a4a] bg-[#2d2d2d]"
                    >
                      <a href={previewUrl} target="_blank" rel="noreferrer">
                        <img
                          src={previewUrl}
                          alt={`Slide ${idx + 1}`}
                          className={cn(
                            "w-full bg-[#1a1a1a] object-contain",
                            asset.aspect_ratio === "1:1"
                              ? "aspect-square"
                              : "aspect-[9/16]",
                          )}
                        />
                      </a>
                      <div className="flex items-center justify-between gap-2 p-3">
                        <span className="text-sm text-[#ccc]">
                          Slide {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            triggerAuthenticatedDownload(downloadUrl, filename)
                          }}
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#555] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#333]"
                        >
                          <Download className="h-3.5 w-3.5" aria-hidden />
                          Download
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
